import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { formatDate, formatOrderTotal } from '~/utils';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import { Button } from '~/components/ui';
import { OrderCardDetails } from '~/pages/orders/sections/OrderCardDetails';
import {
  NO_PRODUCTS,
  type OrderCardProduct,
  type OrderCardPlant,
} from '~/pages/orders/lib/orderCardProducts';

export function SellerOrderCard({
  id,
  status,
  orderDate,
  buyingOrganizationName,
  itemCount,
  totalAmount,
  currency,
  products = NO_PRODUCTS,
  plant,
  deliveryLocation,
  onClick,
}: {
  id: string;
  status: string;
  orderDate: string;
  buyingOrganizationName: string;
  itemCount: number;
  totalAmount: number | null;
  currency?: string;
  products?: OrderCardProduct[];
  plant?: OrderCardPlant | null;
  deliveryLocation?: string | null;
  onClick?: () => void;
}) {
  const { t } = useTranslation('order');
  return (
    <div
      data-testid="order-card"
      className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3
              className="text-neut-prim font-semibold"
              data-testid="order-card-id"
            >
              {id}
            </h3>
            <OrderStatusBadge status={status} />
          </div>
          <p className="text-sm text-gray-500 font-normal">
            {t('order.seller_order_date', { orderDate: formatDate(orderDate) })}
          </p>
        </div>
        <Button variant="ghost-primary" width="10rem" onClick={onClick}>
          <Eye size="16" />
          {t('order.view_details')}
        </Button>
      </div>
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm text-gray-600 mb-3 font-normal">
          {t('order.customer')}:{' '}
          <span className="text-neut-prim font-medium">
            {buyingOrganizationName}
          </span>
        </p>
        <OrderCardDetails
          products={products}
          plant={plant}
          deliveryLocation={deliveryLocation}
        />
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 font-normal">
            {t('order.item_count', { count: itemCount })}
          </span>
          <span className="text-neut-prim font-semibold">
            {formatOrderTotal(totalAmount, t('order.not_available'), currency)}
          </span>
        </div>
      </div>
    </div>
  );
}
